import React, { useMemo } from 'react';
import { Content } from '@amfintech/refine-react-admin';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DateRangeField,
  formatNumber,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@amfintech/react-admin-ui';
import { useCustom, useNotification } from '@refinedev/core';
import dayjs from 'dayjs';

// ============================================================
// Types
// ============================================================

interface StatsRow {
  [key: string]: string | number;
}

interface StatCardProps {
  title: string;
  value?: number | string;
  rows?: StatsRow[];
  className?: string;
  action?: React.ReactNode;
}

interface TransactionInitiatedRow {
  type: string;
  count: number;
  PendingSubmission: number;
  PendingOpsConfirmation: number;
  PendingPaymentVerification: number;
  Approved: number;
  Succeeded: number;
  Failed: number;
  Rejected: number;
  Cancelled: number;
  Void: number;
}

interface TransactionStatusBreakdown {
  total: number;
  Approved: number;
  Pending: number;
  Succeeded: number;
  Failed: number;
  Rejected: number;
  Cancelled: number;
  Void: number;
  PendingSubmission: number;
  PendingOpsConfirmation: number;
  PendingPaymentVerification: number;
}

interface TransactionStatsByTypeRow {
  transactionType: string;
  stats: TransactionStatusBreakdown;
}

interface CountRow {
  key: string;
  count: number;
}

interface LabelValueRow {
  label: string;
  value: number;
  [key: string]: string | number;
}

interface DailyStatisticsInRange {
  transactions: {
    transactionTypeRows: TransactionStatsByTypeRow[];
  };
  users: {
    invitationCodeCount: number;
    usersOnboarded: number;
    companiesOnboarded: number;
  };
  activity: {
    loginCount: number;
    statementViews: number;
  };
}

interface TransactionStatsAllTime {
  transactionTypeRows: CountRow[];
  transactionStatusRows: CountRow[];
}

interface OrderStatsAllTime {
  orderStatusRows: CountRow[];
}

interface UserStatsAllTime {
  userRows: LabelValueRow[];
  onboardedCompanyCount: number;
}

interface OnboardedCompanyRow {
  companyName: string;
  idNo: string;
  onboardedDate: string;
}

interface TransactionInitiatedExportRow {
  transactionReferenceNo: string;
  orderReferenceNo?: string;
  transactionDate: string;
  action: string;
  customerName: string;
  masterAccountNumber: string;
  fundCode: string;
  fundName: string;
  amount: string;
  quantity: string;
  currencyCode?: string;
  transactionStatus: string;
}

const EXCLUDED_ORDER_STATUS_ROWS = new Set(['Created', 'Pending', 'PendingSignature']);

const formatStatsLabel = (label: string) => label.replace(/([a-z])([A-Z])/g, '$1 $2');

const escapeCsvValue = (value: string | number | null | undefined) => {
  const text = value == null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadCsv = (rows: Array<Record<string, string | number>>, filename: string) => {
  const headers = Object.keys(rows[0] ?? { 'Company Name': '', 'ID Number': '', 'Onboarded Date': '' });
  const csvContent = [
    headers.map(escapeCsvValue).join(','),
    ...rows.map((row) => headers.map((header) => escapeCsvValue(row[header])).join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ============================================================
// Components
// ============================================================

export function StatCard({ title, value, rows, className, action }: StatCardProps) {
  return (
    <Card className={className ?? 'h-full'}>
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-sm">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardContent>
        {rows && rows.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                {Object.keys(rows[0]).map((col) => (
                  <TableHead key={col} className={col === 'label' ? '' : 'text-right'}>
                    {col.charAt(0).toUpperCase() + col.slice(1)}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Object.values(row).map((val, colIndex) => (
                    <TableCell key={colIndex} className={colIndex === 0 ? '' : 'text-right'}>
                      {val}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="text-2xl font-bold">{value ?? 0}</div>
        )}
      </CardContent>
    </Card>
  );
}

function TransactionInitiatedCard({
  rows,
  isLoading,
  action,
}: {
  rows: TransactionInitiatedRow[];
  isLoading: boolean;
  action?: React.ReactNode;
}) {
  return (
    <Card className="col-span-1 md:col-span-3 xl:col-span-3">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle className="text-sm">Transactions initiated</CardTitle>
        {action}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-2xl font-bold">...</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Count</TableHead>
                <TableHead className="text-right">Pending Submission</TableHead>
                <TableHead className="text-right">Pending Ops Confirmation</TableHead>
                <TableHead className="text-right">Pending Payment Verification</TableHead>
                <TableHead className="text-right">Approved</TableHead>
                <TableHead className="text-right">Succeeded</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Rejected</TableHead>
                <TableHead className="text-right">Cancelled</TableHead>
                <TableHead className="text-right">Void</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.type}>
                  <TableCell>{row.type}</TableCell>
                  <TableCell className="text-right">{row.count}</TableCell>
                  <TableCell className="text-right">{row.PendingSubmission}</TableCell>
                  <TableCell className="text-right">{row.PendingOpsConfirmation}</TableCell>
                  <TableCell className="text-right">{row.PendingPaymentVerification}</TableCell>
                  <TableCell className="text-right">{row.Approved}</TableCell>
                  <TableCell className="text-right">{row.Succeeded}</TableCell>
                  <TableCell className="text-right">{row.Failed}</TableCell>
                  <TableCell className="text-right">{row.Rejected}</TableCell>
                  <TableCell className="text-right">{row.Cancelled}</TableCell>
                  <TableCell className="text-right">{row.Void}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================================
// Main Dashboard
// ============================================================

export default function MonitoringDashboard() {
  // Date range state
  const [from, setFrom] = React.useState<dayjs.Dayjs | undefined>(dayjs().startOf('day'));
  const [to, setTo] = React.useState<dayjs.Dayjs | undefined>(dayjs().endOf('day'));
  const notification = useNotification();
  
  // All time stats load control
  const [shouldLoadTransactionsAllTime, setShouldLoadTransactionsAllTime] = React.useState(false);
  const [shouldLoadOrdersAllTime, setShouldLoadOrdersAllTime] = React.useState(false);
  const [shouldLoadUsersAllTime, setShouldLoadUsersAllTime] = React.useState(false);

  // =========================================================
  // In-Range Stats (single consolidated query)
  // =========================================================
  const { data: statsInRangeResult, isLoading: isLoadingInRange } = useCustom<DailyStatisticsInRange>({
    method: 'get',
    url: '',
    meta: {
      operation: 'dailyStatisticsInRange',
      fields: [
        {
          transactions: [
            {
              transactionTypeRows: [
                'transactionType',
                {
                  stats: [
                    'total',
                    'Approved',
                    'Succeeded',
                    'Failed',
                    'Rejected',
                    'Cancelled',
                    'Void',
                    'PendingSubmission',
                    'PendingOpsConfirmation',
                    'PendingPaymentVerification',
                  ],
                },
              ],
            },
          ],
        },
        {
          users: ['invitationCodeCount', 'usersOnboarded', 'companiesOnboarded'],
        },
        {
          activity: ['loginCount', 'statementViews'],
        },
      ],
      variables: {
        from: { type: 'DateTime!', value: from?.toDate() },
        to: { type: 'DateTime!', value: to?.toDate() },
      },
    },
    queryOptions: {
      enabled: !!from && !!to,
    },
  });

  const { refetch: refetchOnboardedCompanies, isFetching: isDownloadingCompanies } = useCustom<
    OnboardedCompanyRow[]
  >({
    method: 'get',
    url: '',
    meta: {
      operation: 'onboardedCompaniesInRange',
      fields: ['companyName', 'idNo', 'onboardedDate'],
      variables: {
        from: { type: 'DateTime!', value: from?.toDate() },
        to: { type: 'DateTime!', value: to?.toDate() },
      },
    },
    queryOptions: {
      enabled: false,
    },
  });

  const { refetch: refetchTransactionInitiatedRows, isFetching: isDownloadingTransactions } = useCustom<
    TransactionInitiatedExportRow[]
  >({
    method: 'get',
    url: '',
    meta: {
      operation: 'transactionInitiatedRowsInRange',
      fields: [
        'transactionReferenceNo',
        'orderReferenceNo',
        'transactionDate',
        'action',
        'customerName',
        'masterAccountNumber',
        'fundCode',
        'fundName',
        'amount',
        'quantity',
        'currencyCode',
        'transactionStatus',
      ],
      variables: {
        from: { type: 'DateTime!', value: from?.toDate() },
        to: { type: 'DateTime!', value: to?.toDate() },
      },
    },
    queryOptions: {
      enabled: false,
    },
  });

  // =========================================================
  // All-Time Stats — Transactions
  // =========================================================
  const { data: transactionsAllTimeResult, isLoading: isLoadingTransactionsAllTime } = useCustom<TransactionStatsAllTime>({
    method: 'get',
    url: '',
    meta: {
      operation: 'transactionStatsAllTime',
      fields: [
        {
          transactionTypeRows: ['key', 'count'],
        },
        {
          transactionStatusRows: ['key', 'count'],
        },
      ],
    },
    queryOptions: {
      enabled: shouldLoadTransactionsAllTime,
    },
  });

  // =========================================================
  // All-Time Stats — Orders
  // =========================================================
  const { data: ordersAllTimeResult, isLoading: isLoadingOrdersAllTime } = useCustom<OrderStatsAllTime>({
    method: 'get',
    url: '',
    meta: {
      operation: 'orderStatsAllTime',
      fields: [
        {
          orderStatusRows: ['key', 'count'],
        },
      ],
    },
    queryOptions: {
      enabled: shouldLoadOrdersAllTime,
    },
  });

  // =========================================================
  // All-Time Stats — Users & Companies
  // =========================================================
  const { data: usersAllTimeResult, isLoading: isLoadingUsersAllTime } = useCustom<UserStatsAllTime>({
    method: 'get',
    url: '',
    meta: {
      operation: 'userStatsAllTime',
      fields: [
        {
          userRows: ['label', 'value'],
        },
        'onboardedCompanyCount',
      ],
    },
    queryOptions: {
      enabled: shouldLoadUsersAllTime,
    },
  });

  // =========================================================
  // Transform data for display
  // =========================================================
  const statsInRange = statsInRangeResult?.data;
  const statsTransactionsAllTime = transactionsAllTimeResult?.data;
  const statsOrdersAllTime = ordersAllTimeResult?.data;
  const statsUsersAllTime = usersAllTimeResult?.data;

  const transactionTypeRowsInRange = useMemo(
    () =>
      statsInRange?.transactions?.transactionTypeRows?.map((row) => ({
        type: row.transactionType,
        count: row.stats.total ?? 0,
        PendingSubmission: row.stats.PendingSubmission ?? 0,
        PendingOpsConfirmation: row.stats.PendingOpsConfirmation ?? 0,
        PendingPaymentVerification: row.stats.PendingPaymentVerification ?? 0,
        Approved: row.stats.Approved ?? 0,
        Succeeded: row.stats.Succeeded ?? 0,
        Failed: row.stats.Failed ?? 0,
        Rejected: row.stats.Rejected ?? 0,
        Cancelled: row.stats.Cancelled ?? 0,
        Void: row.stats.Void ?? 0,
      })) ?? [],
    [statsInRange],
  );

  const transactionTypeRowsAllTime = useMemo(
    () =>
      statsTransactionsAllTime?.transactionTypeRows?.map((row) => ({
        label: formatStatsLabel(row.key),
        value: row.count,
      })) ?? [],
    [statsTransactionsAllTime],
  );

  const transactionStatusRowsAllTime = useMemo(
    () =>
      statsTransactionsAllTime?.transactionStatusRows
        ?.filter((row) => row.key !== 'Submitted')
        .map((row) => ({
          label: formatStatsLabel(row.key),
          value: row.count,
        })) ?? [],
    [statsTransactionsAllTime],
  );

  const orderStatusRowsAllTime = useMemo(
    () =>
      statsOrdersAllTime?.orderStatusRows
        ?.filter((row) => !EXCLUDED_ORDER_STATUS_ROWS.has(row.key))
        .map((row) => ({
          label: formatStatsLabel(row.key),
          value: row.count,
        })) ?? [],
    [statsOrdersAllTime],
  );

  const userRowsAllTime = useMemo(() => statsUsersAllTime?.userRows ?? [], [statsUsersAllTime]);

  const handleDownloadOnboardedCompanies = React.useCallback(async () => {
    if (!from || !to) {
      notification.open?.({
        type: 'error',
        message: 'Please select a date range before downloading.',
      });
      return;
    }

    try {
      const result = await refetchOnboardedCompanies();

      const rows = ((result.data?.data ?? []) as OnboardedCompanyRow[]).map((row) => ({
        'Company Name': row.companyName,
        'ID Number': row.idNo,
        'Onboarded Date': dayjs(row.onboardedDate).format('DD MMM YYYY'),
      }));

      const filename = `companies_onboarded_${from.format('YYYYMMDD')}_${to.format('YYYYMMDD')}.csv`;
      downloadCsv(rows, filename);
    } catch (error) {
      notification.open?.({
        type: 'error',
        message: 'Failed to download companies onboarded.',
      });
    }
  }, [from, notification, refetchOnboardedCompanies, to]);

  const handleDownloadTransactionInitiatedRows = React.useCallback(async () => {
    if (!from || !to) {
      notification.open?.({
        type: 'error',
        message: 'Please select a date range before downloading.',
      });
      return;
    }

    try {
      const result = await refetchTransactionInitiatedRows();

      const rows = ((result.data?.data ?? []) as TransactionInitiatedExportRow[]).map((row) => ({
        'Transaction Ref. No': row.transactionReferenceNo,
        'Order Ref. No': row.orderReferenceNo ?? '',
        'Transaction Date': dayjs(row.transactionDate).format('DD MMM YYYY'),
        Action: row.action,
        'Client/ Customer Name': row.customerName,
        'Master Account Number': row.masterAccountNumber,
        'Fund Code': row.fundCode,
        'Fund Name': row.fundName,
        'Amount/Unit':
          row.action === 'Buy'
            ? `${row.currencyCode ?? ''} ${formatNumber(row.amount)}`.trim()
            : `${formatNumber(row.quantity)} units`,
        'Transaction Status': row.transactionStatus,
      }));

      const filename = `transactions_initiated_${from.format('YYYYMMDD')}_${to.format('YYYYMMDD')}.csv`;
      downloadCsv(rows, filename);
    } catch (error) {
      notification.open?.({
        type: 'error',
        message: 'Failed to download transactions initiated.',
      });
    }
  }, [from, notification, refetchTransactionInitiatedRows, to]);

  // =========================================================
  // UI
  // =========================================================
  return (
    <Content title="Monitoring Statistics">
      <div className="w-full grid gap-5">
        {/* =====================================================
         * SECTION 1 — DATE RANGE
         * ===================================================== */}
        <Card>
          <CardHeader className="flex flex-col gap-2">
            <CardTitle>Date Range Analytics</CardTitle>
            <div className="text-xs text-gray-500">Metrics calculated within selected time range</div>
          </CardHeader>

          <CardContent className="space-y-4 pt-0">
            <div className="sm:col-span-2 flex justify-start">
              <DateRangeField
                value={{ from: from?.toDate() ?? undefined, to: to?.toDate() ?? undefined }}
                onValue={(date) => {
                  if (!date) {
                    setFrom(undefined);
                    setTo(undefined);
                    return;
                  }
                  setFrom(date.from ? dayjs(date.from).startOf('day') : undefined);
                  setTo(date.to ? dayjs(date.to).endOf('day') : undefined);
                }}
                size="md"
                placeholder="Pick a date range"
              />
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 items-start">
              <StatCard
                title="Companies Onboarded"
                value={isLoadingInRange ? '...' : statsInRange?.users?.companiesOnboarded}
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!from || !to || isDownloadingCompanies}
                    onClick={handleDownloadOnboardedCompanies}
                  >
                    {isDownloadingCompanies ? 'Downloading...' : 'Download'}
                  </Button>
                }
              />
              <StatCard
                title="Users Onboarded"
                value={isLoadingInRange ? '...' : statsInRange?.users?.usersOnboarded}
              />
              <StatCard
                title="Invitation Codes Sent"
                value={isLoadingInRange ? '...' : statsInRange?.users?.invitationCodeCount}
              />
              <StatCard
                title="Login Count"
                value={isLoadingInRange ? '...' : statsInRange?.activity?.loginCount}
              />
              <TransactionInitiatedCard
                rows={transactionTypeRowsInRange}
                isLoading={isLoadingInRange}
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!from || !to || isDownloadingTransactions}
                    onClick={handleDownloadTransactionInitiatedRows}
                  >
                    {isDownloadingTransactions ? 'Downloading...' : 'Download'}
                  </Button>
                }
              />
              <StatCard
                title="Statements Viewed"
                value={isLoadingInRange ? '...' : statsInRange?.activity?.statementViews}
              />
            </div>
          </CardContent>
        </Card>

        {/* =====================================================
         * SECTION 2 — ALL TIME
         * ===================================================== */}
        <Card>
          <CardHeader className="flex flex-col gap-3 space-y-0 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle>All Time Analytics</CardTitle>
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 lg:w-auto">
              <Button
                onClick={() => setShouldLoadTransactionsAllTime(true)}
                disabled={shouldLoadTransactionsAllTime}
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap"
              >
                {shouldLoadTransactionsAllTime && isLoadingTransactionsAllTime
                  ? 'Loading Transactions...'
                  : shouldLoadTransactionsAllTime
                    ? 'Transactions Loaded'
                    : 'Load Transaction Stats'}
              </Button>
              <Button
                onClick={() => setShouldLoadOrdersAllTime(true)}
                disabled={shouldLoadOrdersAllTime}
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap"
              >
                {shouldLoadOrdersAllTime && isLoadingOrdersAllTime
                  ? 'Loading Orders...'
                  : shouldLoadOrdersAllTime
                    ? 'Orders Loaded'
                    : 'Load Order Stats'}
              </Button>
              <Button
                onClick={() => setShouldLoadUsersAllTime(true)}
                disabled={shouldLoadUsersAllTime}
                variant="outline"
                size="sm"
                className="w-full whitespace-nowrap"
              >
                {shouldLoadUsersAllTime && isLoadingUsersAllTime
                  ? 'Loading Users/Companies...'
                  : shouldLoadUsersAllTime
                    ? 'Users/Companies Loaded'
                    : 'Load User/Company Stats'}
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <StatCard
                title="Transactions (By Status)"
                rows={isLoadingTransactionsAllTime ? [] : transactionStatusRowsAllTime}
              />
              <StatCard
                title="Transactions (By Type)"
                rows={isLoadingTransactionsAllTime ? [] : transactionTypeRowsAllTime}
              />
              <StatCard title="Orders (By Status)" rows={isLoadingOrdersAllTime ? [] : orderStatusRowsAllTime} />
              <StatCard title="Users" rows={isLoadingUsersAllTime ? [] : userRowsAllTime} />
              <StatCard
                title="Companies"
                rows={[
                  {
                    label: 'Companies Onboarded',
                    value: isLoadingUsersAllTime ? 0 : (statsUsersAllTime?.onboardedCompanyCount ?? 0),
                  },
                ]}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Content>
  );
}
